/**
 * fillMissingQuarters
 *
 * From a list of quarters, fills in any missing quarters in the middle of the data.
 *
 * @param data Any list with the `quarter` property
 * @param getDefaultValues
 * @returns
 */
export const fillMissingQuarters = <T extends { quarter: string }>(
    data: T[],
    getDefaultValues: (quarter: string) => Omit<T, "quarter">,
): T[] => {
    if (data.length === 0) return [];

    // Sort by quarter
    const sortedData = [...data].sort((a, b) =>
        a.quarter.localeCompare(b.quarter),
    );

    const parseQuarter = (q: string) => {
        const [year, quarter] = q.split("-Q");
        return { year: parseInt(year, 10), quarter: parseInt(quarter, 10) };
    };

    const formatQuarter = (year: number, quarter: number) =>
        `${year}-Q${quarter}`;

    const getNextQuarter = (year: number, quarter: number) => {
        if (quarter === 4) return { year: year + 1, quarter: 1 };
        return { year, quarter: quarter + 1 };
    };

    const result: T[] = [];
    let current = parseQuarter(sortedData[0].quarter);
    const end = parseQuarter(sortedData[sortedData.length - 1].quarter);

    while (
        current.year < end.year ||
        (current.year === end.year && current.quarter <= end.quarter)
    ) {
        const quarterStr = formatQuarter(current.year, current.quarter);
        const existing = sortedData.find((d) => d.quarter === quarterStr);

        if (existing) {
            result.push(existing);
        } else {
            result.push({
                quarter: quarterStr,
                ...getDefaultValues(quarterStr),
            } as T);
        }

        current = getNextQuarter(current.year, current.quarter);
    }

    return result;
};
